/*
 * Hat rendering — one Graphics builder per hat type.
 *
 * Each renderer mutates a Graphics object centered on (0, 0) with "up" being -y,
 * mirroring the local frame of the critter head. The parent critter container
 * positions/scales the hat as a whole.
 *
 * Animated hats (firefly) accept a time param so the parent ticker can call them
 * each frame; static hats ignore it.
 */

import { Graphics } from 'pixi.js';
import { drawStar } from '@/render/draw';

export function buildHat(name: string, g: Graphics, t: number): void {
  g.clear();
  switch (name) {
    case 'acorn': {
      g.ellipse(0, 1, 11, 7).fill(0x6b4423);
      g.circle(0, -4, 1.8).fill(0x3a2410);
      g.moveTo(-11, 1).lineTo(11, 1).stroke({ color: 0x000000, alpha: 0.25, width: 0.6 });
      break;
    }
    case 'mushroom': {
      g.ellipse(0, -1, 13, 9).fill(0xc9514c);
      g.circle(-5, -3, 1.7).fill(0xfff4dc);
      g.circle(4, -5, 1.5).fill(0xfff4dc);
      g.circle(1, -7, 1.3).fill(0xfff4dc);
      break;
    }
    case 'leaf': {
      // Rotated ellipse — Pixi's .ellipse doesn't take rotation, so we draw a
      // path approximation: simple rotated ellipse via Graphics.matrix would
      // work but is overkill; this looks fine.
      const angle = -0.4;
      const rx = 12;
      const ry = 5.5;
      const cx = -1;
      const cy = 0;
      g.beginPath();
      for (let i = 0; i <= 32; i++) {
        const a = (i / 32) * Math.PI * 2;
        const ex = Math.cos(a) * rx;
        const ey = Math.sin(a) * ry;
        const px = cx + ex * Math.cos(angle) - ey * Math.sin(angle);
        const py = cy + ex * Math.sin(angle) + ey * Math.cos(angle);
        if (i === 0) g.moveTo(px, py);
        else g.lineTo(px, py);
      }
      g.fill(0x5a8a3a);
      g.moveTo(-11, 1.5).lineTo(8, -3).stroke({ color: 0x3a6020, width: 0.7 });
      break;
    }
    case 'star': {
      drawStar(g, 0, -1, 5, 7, 3);
      g.fill(0xffc56b);
      // Subtle glow ring (cheap shadow analog).
      g.circle(0, -1, 8).fill({ color: 0xffe9a8, alpha: 0.18 });
      break;
    }
    case 'flower': {
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const px = Math.cos(a) * 5;
        const py = -2 + Math.sin(a) * 3;
        g.ellipse(px, py, 3.5, 2.5).fill(0xfff4dc);
      }
      g.circle(0, -2, 2.5).fill(0xffc56b);
      g.moveTo(0, 0).lineTo(-1, 4).stroke({ color: 0x5a8a3a, width: 1 });
      break;
    }
    case 'crown': {
      g.moveTo(-10, 2)
        .lineTo(-10, -2)
        .lineTo(-6, -5)
        .lineTo(-3, 0)
        .lineTo(0, -7)
        .lineTo(3, 0)
        .lineTo(6, -5)
        .lineTo(10, -2)
        .lineTo(10, 2)
        .closePath()
        .fill(0xffd76b);
      g.circle(0, -4, 1.2).fill(0xc9514c);
      g.circle(-6, -3, 0.9).fill(0x4a7090);
      g.circle(6, -3, 0.9).fill(0x4a7090);
      g.moveTo(-8, 1).lineTo(-6, 0).stroke({ color: 0xffffff, alpha: 0.5, width: 0.6 });
      break;
    }
    case 'bow': {
      g.moveTo(0, 0).lineTo(-8, -5).lineTo(-8, 1).closePath().fill(0xc9514c);
      g.moveTo(0, 0).lineTo(8, -5).lineTo(8, 1).closePath().fill(0xc9514c);
      g.circle(0, -1, 2.5).fill(0xa83a36);
      g.circle(-5, -2, 0.7).fill(0xfff4dc);
      g.circle(5, -2, 0.7).fill(0xfff4dc);
      break;
    }
    case 'pinecone helm': {
      // Cream chin strap, drawn FIRST so the pinecone scales overlap it.
      g.moveTo(-9, 5).quadraticCurveTo(0, 16, 9, 5).stroke({ color: 0xf4ecd8, width: 1.4 });
      // Pinecone dome — warm umber base.
      g.ellipse(0, -3, 11, 9).fill(0x7a4a2e);
      // Overlapping triangular scales in three staggered rows.
      const scale = (sx: number, sy: number): void => {
        g.moveTo(sx - 2.5, sy)
          .lineTo(sx, sy - 2.2)
          .lineTo(sx + 2.5, sy)
          .closePath()
          .fill(0x5c3820);
      };
      // Top row.
      scale(-4, -6); scale(0, -7); scale(4, -6);
      // Middle row.
      scale(-6, -3); scale(-2, -4); scale(2, -4); scale(6, -3);
      // Bottom row.
      scale(-5, 0); scale(0, -1); scale(5, 0);
      // Subtle highlight on the windward side.
      g.ellipse(-3, -6, 2.5, 1).fill({ color: 0xa86b3c, alpha: 0.5 });
      break;
    }
    case 'shooting star': {
      // Tiny stylized comet sitting on the head — bright head + soft glow + short tail.
      // Tail trails slightly to the upper-right so the silhouette reads at 30 px.
      const headX = 2;
      const headY = -3;
      const tailDx = -6;
      const tailDy = 3;
      // Soft halo.
      g.circle(headX, headY, 7).fill({ color: 0xffe9b3, alpha: 0.22 });
      // Tail sparks (4 fading dots).
      for (let i = 1; i <= 4; i++) {
        const tx = headX + (tailDx * i) / 4;
        const ty = headY + (tailDy * i) / 4;
        const a = 0.7 - i * 0.15;
        g.circle(tx, ty, 1.1 + (4 - i) * 0.3).fill({ color: 0xfff7e0, alpha: a });
      }
      // Bright head + hot core.
      g.circle(headX, headY, 3).fill({ color: 0xfff7e0, alpha: 1 });
      g.circle(headX, headY, 1.2).fill({ color: 0xffffff, alpha: 1 });
      break;
    }
    case 'firefly': {
      const phase = t * 0.005;
      const fy = -5 + Math.sin(phase) * 1.5;
      const fx = Math.cos(phase * 0.7) * 4;
      g.circle(fx, fy, 8).fill({ color: 0xfff3a0, alpha: 0.4 });
      g.circle(fx, fy, 4).fill({ color: 0xfff3a0, alpha: 0.7 });
      g.circle(fx, fy, 1.5).fill(0xffffff);
      g.circle(-3, 0, 0.8).fill({ color: 0xfff3a0, alpha: 0.3 });
      g.circle(3, -2, 0.6).fill({ color: 0xfff3a0, alpha: 0.3 });
      break;
    }
    case 'none':
    default:
      // Nothing to draw — bare-head critter.
      break;
  }
}

/** Hats that need redrawing every frame (only the firefly hat is animated). */
export function isAnimatedHat(name: string): boolean {
  return name === 'firefly';
}
